/**
 * A choice element's values, and "Allow multiple" (docs/plans/data-types-second-
 * pass.md §3.5; the owner's rulings D1 — an option on a Choice, named "Allow
 * multiple" — and D2 — entries in the CHOICES-LIST order, never entry order).
 *
 * The API owns the rule (keepr-api utils/choiceValues.js) and canonicalises
 * every write; this module is the browser's reading of the same shapes, so the
 * form sends the list already in order, every surface reads a scalar and a
 * list alike, and the editor can say what an option change does to a default:
 *
 *   - a multi element holds an ARRAY of choice values, once each, in the
 *     choices' order; `[]` is written as '' (the one emptiness);
 *   - a scalar under a multi element reads as a list of one, and a list under
 *     a single element reads as its first entry — every reader takes both
 *     shapes, as a SAFETY NET only: since the owner's ruling B16 turning the
 *     switch on or off converts the stored values with the save
 *     (utils/shapeChange.js), so neither is the state a flip leaves;
 *   - an entry the list no longer names (its choice was removed) still reads
 *     as it is stored, after the listed ones.
 */

const isBlank = v => v === undefined || v === null || v === '';

/**
 * The one dataType on which `options.choices` is the element's vocabulary
 * (the owner's ruling, 2026-10-19, knowledge rules/choices-only-on-choice-
 * elements; KPE-033). A list on ANY other type is inert: the card editor keeps
 * it so a change back to Choice can reuse it (elementOptionPolicy
 * pruneOptionsForType), and every reader ignores it. A short text with a list
 * is a short text — the legacy reading (KPR-142) is gone, and the migration
 * (KPR-1012, run on production as KPR-1013) converted every one there was.
 */
const CHOICE_DATA_TYPES = new Set(['choice']);

/**
 * The element's choice list when it means something — a Choice element's own
 * `options.choices` — and `[]` on every other type, whatever list it carries.
 * Every reader that offers, labels, colours or counts choices asks here.
 */
export const choiceListOf = element => (
	CHOICE_DATA_TYPES.has(element?.dataType) && Array.isArray(element?.options?.choices) ? element.options.choices : []
);

/** Does this element have choices that mean something (a Choice with at least one)? */
export const elementHasChoices = element => choiceListOf(element).length > 0;

/**
 * Should this element (or a filter field built from one) be read and edited as
 * a choice? THE platform rule, asked by every typed value control: the item
 * form, the condition builder, the expected-item rule editor and the list
 * filter builder (`kqlBuilder.fieldKindOf`). A Choice element with a list,
 * and nothing else: a short text, number, rating or date that carries a list
 * (an inert one, kept from an earlier life as a Choice) is a short text,
 * number, rating or date.
 */
export const rendersAsChoice = element => elementHasChoices(element);

/**
 * A list no reader uses: `options.choices` on any type but Choice, kept for a
 * change back to Choice. [] on a Choice, and on an element with no list.
 */
export const inertChoicesOf = element => (
	!CHOICE_DATA_TYPES.has(element?.dataType) && Array.isArray(element?.options?.choices) ? element.options.choices : []
);

/** Does this element hold a LIST of choices? A Choice only — never a short text with choices. */
export const allowsMultipleChoices = element => element?.dataType === 'choice' && element?.options?.allowMultiple === true;

/** The element's choice values, in list order (legacy bare strings included). */
export function choiceValueList(element) {
	return choiceListOf(element)
		.map(c => (c !== null && typeof c === 'object' ? c.value : c))
		.filter(v => !isBlank(v))
		.map(v => String(v));
}

/** Every entry a stored value holds, as strings: one for a scalar, the list for an array, [] for empty. */
export function choiceEntries(raw) {
	if (isBlank(raw)) return [];
	const list = Array.isArray(raw) ? raw : [raw];
	const out = [];
	list.forEach((entry) => {
		if (isBlank(entry) || typeof entry === 'object') return;
		const text = String(entry);
		if (!out.includes(text)) out.push(text);
	});
	return out;
}

/** The entries in the choices' order; entries the list does not name follow, as given (D2). */
export function canonicalChoices(element, raw) {
	const picked = choiceEntries(raw);
	const values = choiceValueList(element);
	return [...values.filter(v => picked.includes(v)), ...picked.filter(v => !values.includes(v))];
}

/** Is this value a list a Choice holds (a multi choice, or a list left on a single one)? Never another type's, whatever list it carries. */
export const isChoiceList = (element, value) => Array.isArray(value) && CHOICE_DATA_TYPES.has(element?.dataType);

/** One choice's label — the value itself when the list no longer names it. */
export function choiceLabelOf(element, value) {
	const found = choiceListOf(element).find(c => (
		c !== null && typeof c === 'object' ? String(c.value) === String(value) : String(c) === String(value)
	));
	if (found === undefined) return String(value);
	if (found !== null && typeof found === 'object') return isBlank(found.label) ? String(found.value) : String(found.label);
	return String(found);
}

/** The value as a person reads it: every entry's label, joined with ", ". */
export const choiceLabelsText = (element, raw) => canonicalChoices(element, raw).map(v => choiceLabelOf(element, v)).join(', ');

/**
 * What an element's DEFAULT becomes when "Allow multiple" is turned on or off,
 * so the editor never saves a default the API refuses (a list default on a
 * single choice is 400 invalid_options): on, a single default becomes a list
 * of one; off, a list default keeps its first entry in the choices' order.
 * undefined when there is no default.
 */
export function defaultForMultiToggle(element, nextMulti) {
	const raw = element?.options?.defaultValue;
	const entries = canonicalChoices(element, raw);
	if (!entries.length) return undefined;
	return nextMulti ? entries : entries[0];
}

// The type changes whose effect on stored values the card editor says in words
// (KPE-033): a short text made a Choice, and a Choice made a short or long text.
const TEXT_TO_CHOICE_SOURCES = ['text-small'];
const CHOICE_TO_TEXT_TARGETS = ['text-small', 'text-large'];

/**
 * What a saved element's type change does to the values its items hold, in
 * words, for the two changes between text and Choice; null for every other.
 *
 *   short text -> Choice   the API reuses a kept list and follows the
 *                          migration's rulings (KPR-1014, keepr-api
 *                          utils/textChoiceRetype.js): a value matching a
 *                          choice but for its capitals becomes that choice,
 *                          and any other value is added as a choice — unless
 *                          that would add more than 200, or show values to
 *                          people who cannot read the items holding them,
 *                          when the save is refused (409
 *                          `choice_values_off_list`, its sentence shown as
 *                          every refused save's is). The words never promise
 *                          more than that.
 *   Choice -> text         each item keeps what it holds, as text — the
 *                          choice's STORED value, which the editor derives
 *                          from the label and never shows, so the sentence
 *                          gives the element's own first choice whose label
 *                          and value differ as the example. A Choice that
 *                          allows multiple keeps its first (the B16 'first'
 *                          answer; the dialog asks it on its own).
 */
export function choiceTypeChangeWords(savedElement, nextType) {
	const from = savedElement?.dataType || 'text-small';
	if (!savedElement || from === nextType) return null;
	if (nextType === 'choice' && TEXT_TO_CHOICE_SOURCES.includes(from)) {
		return "A value that matches a choice but for its capitals becomes that choice, and any other value is added to the list as a new choice. If that would add more than 200 choices, or show those values to people who can't read the items holding them, keepr refuses the save and says why.";
	}
	if (from === 'choice' && CHOICE_TO_TEXT_TARGETS.includes(nextType)) {
		const keeps = savedElement?.options?.allowMultiple === true
			? 'Each item keeps its first choice, as text.'
			: 'Each item keeps the choice it holds, as text.';
		const differs = choiceListOf(savedElement).find(choice => (
			choice !== null && typeof choice === 'object' && !isBlank(choice.label) && !isBlank(choice.value)
			&& String(choice.label) !== String(choice.value)
		));
		return differs
			? `${keeps} A choice is kept as its stored value, so “${differs.label}” reads as “${differs.value}”.`
			: keeps;
	}
	return null;
}

/**
 * One element (or column) as a save sends it: a non-Choice element carries
 * EXACTLY the list its saved copy stores, or none. The API keeps an inert list
 * only unchanged and refuses a save that adds or changes one (KPR-1014), and
 * nobody can see or edit an inert list in the editor — so a list that differs
 * was edited while the element was a Choice (a new element made a Choice and
 * then a text, a saved Choice edited and then retyped), and the stored one is
 * what the author's save means.
 */
function inertListAsStored(element, savedElement) {
	const options = element?.options;
	if (!options || typeof options !== 'object' || !('choices' in options) || CHOICE_DATA_TYPES.has(element.dataType)) return element;
	const stored = Array.isArray(savedElement?.options?.choices) ? savedElement.options.choices : undefined;
	if (options.choices === stored) return element;
	if (stored === undefined) {
		const { choices: _dropped, ...rest } = options;
		return { ...element, options: rest };
	}
	return { ...element, options: { ...options, choices: stored } };
}

/**
 * The elements as a card or element set save sends them: every inert list as
 * stored (inertListAsStored), a repeating group's columns too, against the
 * saved group's columns by name. `savedByName` is the saved elements by name
 * (conversions.indexSavedElements) and `names` the name the API will give each
 * element (effectiveElementNames, or namespacedElementNames for a set). An
 * element with no saved copy — a new one, or any on a new card — sends no
 * inert list at all.
 */
export function withInertChoicesAsStored(elements, savedByName, names) {
	if (!Array.isArray(elements)) return elements;
	return elements.map((element, i) => {
		if (!element || typeof element !== 'object') return element;
		const saved = savedByName?.[names?.[i]] || null;
		let next = inertListAsStored(element, saved);
		const columns = next?.options?.columns;
		if (Array.isArray(columns)) {
			const savedColumns = new Map((Array.isArray(saved?.options?.columns) ? saved.options.columns : [])
				.filter(column => column && typeof column.name === 'string' && column.name)
				.map(column => [column.name, column]));
			let changed = false;
			const nextColumns = columns.map((column) => {
				const out = inertListAsStored(column, column && typeof column.name === 'string' ? savedColumns.get(column.name) : null);
				if (out !== column) changed = true;
				return out;
			});
			if (changed) next = { ...next, options: { ...next.options, columns: nextColumns } };
		}
		return next;
	});
}
